import type { KyInstance } from 'ky';

import { ensureError, formatString, isNotDefined } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import type { Status } from '#types/status/status.type';

import { Uri } from '#constants/uri';

import type { GetStatusWithActionsQuery } from './types/get-status-with-actions-query.type';

/**
 * Fetches the status along with available actions from the API.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param query - The query parameters to filter or specify the status request.
 * @returns A promise that resolves to an {@link OperationResultWithData} containing the {@link Status}.
 * @throws Will throw an error if the request fails.
 */
export const getStatusWithActions = async (instance: KyInstance, query: GetStatusWithActionsQuery) => {
  try {
    if (isNotDefined(query.statusId)) {
      throw new Error('Status ID is required to fetch status with actions.');
    }

    return await instance
      .get<OperationResultWithData<Status>>(formatString(Uri.status.withActions, query.statusId.toString()), {
        searchParams: query,
      })
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
